/**
 * APIs Tab Component
 *
 * Displays discovered API endpoints with method, path, and description
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, Code, Search, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { getSystemAPIs } from '../../lib/api/enterpriseSystemsApi';

interface APIsTabProps {
  systemId: string;
}

const APIsTab: React.FC<APIsTabProps> = ({ systemId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedApis, setExpandedApis] = useState<Set<string>>(new Set());

  const { data: apis = [], isLoading, error } = useQuery({
    queryKey: ['system-apis', systemId],
    queryFn: () => getSystemAPIs(systemId),
  });

  const toggleApiExpansion = (apiId: string) => {
    setExpandedApis((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(apiId)) {
        newSet.delete(apiId);
      } else {
        newSet.add(apiId);
      }
      return newSet;
    });
  };

  // Filter APIs based on search
  const filteredAPIs = apis.filter((api: any) =>
    api.api_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    api.path?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    api.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    api.module_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group APIs by Module
  const apisByModule = filteredAPIs.reduce((acc: any, api: any) => {
    const module = api.module_name || 'Other';
    if (!acc[module]) acc[module] = [];
    acc[module].push(api);
    return acc;
  }, {});

  /**
   * Get HTTP method color
   */
  const getMethodColor = (method: string) => {
    switch (method?.toUpperCase()) {
      case 'GET':
        return 'bg-green-100 text-green-800';
      case 'POST':
        return 'bg-blue-100 text-blue-800';
      case 'PUT':
      case 'PATCH':
        return 'bg-yellow-100 text-yellow-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading APIs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading APIs</h3>
            <p className="text-sm text-red-700 mt-1">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (apis.length === 0) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
        <Code className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No API Endpoints Found</h3>
        <p className="text-neutral-600">
          Run AI research to discover API endpoints and documentation
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900">API Endpoints</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Discovered REST/SOAP endpoints ({apis.length} total)
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search endpoints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* APIs List */}
      <div className="space-y-6">
        {Object.entries(apisByModule).map(([module, moduleAPIs]: [string, any]) => (
          <div key={module}>
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">
              {module} ({moduleAPIs.length})
            </h3>
            <div className="space-y-2">
              {moduleAPIs.map((api: any) => {
                const isExpanded = expandedApis.has(api.endpoint_id);
                const parameters = api.parameters ? JSON.parse(api.parameters) : [];
                const hasParameters = parameters.length > 0;

                return (
                  <div
                    key={api.endpoint_id}
                    className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`px-3 py-1 ${getMethodColor(
                          api.method
                        )} text-xs font-bold rounded-md uppercase flex-shrink-0`}
                      >
                        {api.method}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1">
                            {api.api_name && (
                              <div className="font-semibold text-neutral-900 mb-1">
                                {api.api_name}
                              </div>
                            )}
                            <code className="text-sm font-mono text-neutral-700 break-all">
                              {api.path}
                            </code>
                          </div>
                          {api.documentation_url && (
                            <a
                              href={api.documentation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 flex-shrink-0"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Docs
                            </a>
                          )}
                        </div>
                        {api.business_context && (
                          <p className="text-sm text-neutral-600 mb-2">{api.business_context}</p>
                        )}
                        {api.description && (
                          <p className="text-sm text-neutral-600 mb-2">{api.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-neutral-500 mb-2">
                          {api.module_code && <span>Code: {api.module_code}</span>}
                          {hasParameters && (
                            <button
                              onClick={() => toggleApiExpansion(api.endpoint_id)}
                              className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                            >
                              {parameters.length} parameters
                              {isExpanded ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              )}
                            </button>
                          )}
                          {api.authentication_required && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                              🔒 Auth Required
                            </span>
                          )}
                        </div>

                        {/* Expanded Parameters Section */}
                        {isExpanded && hasParameters && (
                          <div className="mt-3 p-3 bg-neutral-50 rounded-md border border-neutral-200">
                            <h4 className="text-xs font-semibold text-neutral-700 mb-2 uppercase">
                              Parameters
                            </h4>
                            <div className="space-y-2">
                              {parameters.map((param: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="bg-white p-2 rounded border border-neutral-200"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <code className="text-xs font-mono text-neutral-900 font-semibold">
                                      {param.name || param.paramName || `param_${idx}`}
                                    </code>
                                    {param.type && (
                                      <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-mono">
                                        {param.type}
                                      </span>
                                    )}
                                  </div>
                                  {param.description && (
                                    <p className="text-xs text-neutral-600 mt-1">
                                      {param.description}
                                    </p>
                                  )}
                                  {param.required !== undefined && (
                                    <span className={`text-xs ${param.required ? 'text-red-600' : 'text-neutral-500'}`}>
                                      {param.required ? 'Required' : 'Optional'}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {filteredAPIs.length === 0 && searchQuery && (
        <div className="text-center py-12 text-neutral-500">
          No APIs match your search
        </div>
      )}
    </div>
  );
};

export default APIsTab;
